import { useState, useCallback, useRef } from 'react';
import { useOnlineStatus } from './useOnlineStatus';

export type SyncState = 'online' | 'saving' | 'synced' | 'offline' | 'error' | 'reconnecting';

export interface SyncStatus {
  /** Current connection/sync state */
  state: SyncState;
  /** Whether browser/network is online */
  isOnline: boolean;
  /** Whether a mutation is in flight */
  isSaving: boolean;
  /** Last successful sync/mutation time (ISO string) */
  lastSyncedAt: string | null;
  /** Error message from last failed mutation */
  errorMessage: string | null;
  /** Call when a mutation starts */
  markSaving: () => void;
  /** Call when a mutation succeeds */
  markSynced: (serverTimeUtc?: string) => void;
  /** Call when a mutation fails */
  markError: (message?: string) => void;
  /** Clear error state (e.g., on retry) */
  clearError: () => void;
  /** Mark connection as reconnecting (SignalR reconnect) */
  markReconnecting: () => void;
  /** Whether the realtime connection is reconnecting */
  isReconnecting: boolean;
}

/**
 * Hook to track online/offline + save/sync state for pages with mutations.
 * Does NOT implement offline queueing.
 */
export const useSyncStatus = (): SyncStatus => {
  const { isOnline } = useOnlineStatus();
  const [isSaving, setIsSaving] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const savingCount = useRef(0);

  const markSaving = useCallback(() => {
    savingCount.current += 1;
    setIsSaving(true);
    setErrorMessage(null);
    setIsReconnecting(false);
  }, []);

  const markSynced = useCallback((serverTimeUtc?: string) => {
    savingCount.current = Math.max(0, savingCount.current - 1);
    if (savingCount.current === 0) {
      setIsSaving(false);
    }
    setLastSyncedAt(serverTimeUtc || new Date().toISOString());
    setErrorMessage(null);
    setIsReconnecting(false);
  }, []);

  const markError = useCallback((message?: string) => {
    savingCount.current = Math.max(0, savingCount.current - 1);
    if (savingCount.current === 0) {
      setIsSaving(false);
    }
    setErrorMessage(message || 'Save failed');
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const markReconnecting = useCallback(() => {
    setIsReconnecting(true);
  }, []);

  // Derive composite state
  let state: SyncState;
  if (!isOnline) {
    state = 'offline';
  } else if (isReconnecting) {
    state = 'reconnecting';
  } else if (isSaving) {
    state = 'saving';
  } else if (errorMessage) {
    state = 'error';
  } else if (lastSyncedAt) {
    state = 'synced';
  } else {
    state = 'online';
  }

  return {
    state,
    isOnline,
    isSaving,
    lastSyncedAt,
    errorMessage,
    markSaving,
    markSynced,
    markError,
    clearError,
    markReconnecting,
    isReconnecting,
  };
};
